import { useState } from 'react';
import { Download, FileImage, FileText, Loader2 } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button } from '@/components/ui/Button';
import { snapshotPage, type SnapshotFormat } from '@/lib/snapshot';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

interface SnapshotMenuProps {
  /** Logical name of the page; becomes the file name root. */
  filename: string;
  /** Optional element selector to capture (defaults to `<main>`). */
  selector?: string;
  className?: string;
}

/**
 * Top-right "Export" menu for a BI page. Lets the user download a PNG or
 * PDF snapshot of the current screen. Renders as a small button that
 * opens a Radix dropdown — origin-aware so it feels anchored.
 */
export function SnapshotMenu({ filename, selector, className }: SnapshotMenuProps) {
  const [busy, setBusy] = useState<SnapshotFormat | null>(null);
  const [open, setOpen] = useState(false);

  async function handle(format: SnapshotFormat) {
    setBusy(format);
    setOpen(false);
    try {
      const target = selector ? (document.querySelector(selector) as HTMLElement | null) : null;
      const stamp = new Date().toISOString().slice(0, 10);
      await snapshotPage({ el: target, filename: `${filename}-${stamp}`, format });
      notify.success(`${format.toUpperCase()} ready`, {
        description: 'Saved to your Downloads folder.',
        duration: 3000,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notify.error('Could not export', { description: msg, duration: 4000 });
    } finally {
      setBusy(null);
    }
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-snapshot-skip="1"
          className={cn('gap-1.5', className)}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-[1000] min-w-[10rem] rounded-md border border-border bg-surface-elevated p-1 shadow-soft outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]',
          )}
        >
          <Item icon={<FileText className="h-3.5 w-3.5" />} label="PDF" onSelect={() => void handle('pdf')} />
          <Item icon={<FileImage className="h-3.5 w-3.5" />} label="PNG" onSelect={() => void handle('png')} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function Item({
  icon,
  label,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        e.preventDefault();
        onSelect();
      }}
      className="press flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none transition-colors data-[highlighted]:bg-primary/10 data-[highlighted]:text-primary"
    >
      {icon}
      <span className="flex-1">{label}</span>
    </DropdownMenu.Item>
  );
}
